import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import "./AddRegistration.css";
import { Helmet } from "react-helmet-async";
import AdminLayout from "../layout/AdminLayout";
import AdminRegistrationContext from "../../context/adminRegistration/adminRegistrationContext";
import AuthContext from "../../context/auth/authContext";
import ErrorLogger from "../ErrorLogger";
import { toast } from "react-toastify";

const EditLELORegistrationProfile = () => {
  const navigate = useNavigate();
  const { id: sessionId } = useParams();
  const authContext = useContext(AuthContext);
  const { user } = authContext;

  const adminRegistrationContext = useContext(AdminRegistrationContext);
  const { updateLELO, LeloRegistrations, getLeloRegistrations } =
    adminRegistrationContext;

  const [formData, setFormData] = useState({
    name: "",
    status: "",
    schoolYear: "",
    enrollmentStart: "",
    enrollmentEnd: "",
    schoolYearStartDate: [{ date: "" }],
    schoolYearEndDate: [{ date: "" }],
    permalink: "",
  });

  const [errors, setErrors] = useState({});

  const {
    name,
    status,
    schoolYear,
    permalink,
    enrollmentStart,
    enrollmentEnd,
    schoolYearStartDate,
    schoolYearEndDate,
  } = formData;


  const sanitizeUrl = (text) => {
    return text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-]/g, "");
  };

  const addDatePair = () => {
    setFormData({
      ...formData,
      schoolYearStartDate: [...schoolYearStartDate, { date: "" }],
      schoolYearEndDate: [...schoolYearEndDate, { date: "" }],
    });
  };

  const removeDatePair = (index) => {
    if (schoolYearStartDate.length > 1) {
      const newStartDates = schoolYearStartDate.filter((_, i) => i !== index);
      const newEndDates = schoolYearEndDate.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        schoolYearStartDate: newStartDates,
        schoolYearEndDate: newEndDates,
      });
    }
  };

  const onChangeDateField = (index, field, value) => {
    if (field === "schoolYearStartDate") {
      const newDates = [...schoolYearStartDate];
      newDates[index] = { date: value };

      // First update form data
      setFormData({ ...formData, schoolYearStartDate: newDates });

      // Validate all start dates at once to avoid stale state
      validateAllStartDates(newDates, schoolYearEndDate);
    } else if (field === "schoolYearEndDate") {
      const newDates = [...schoolYearEndDate];
      newDates[index] = { date: value };
      setFormData({ ...formData, schoolYearEndDate: newDates });
      // Only validate date range for this pair
      validateDatePair(index, schoolYearStartDate[index]?.date, value, schoolYearStartDate, false);
    }
  };

  const validateAllStartDates = (startDatesArray, endDatesArray) => {
    const newErrors = { ...errors };

    // Clear all previous start date errors
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith('schoolYearStartDate_') || key.startsWith('schoolYearDateRange_')) {
        delete newErrors[key];
      }
    });

    // Validate each date pair
    startDatesArray.forEach((startDateObj, i) => {
      const startDate = startDateObj.date;
      const endDate = endDatesArray[i]?.date;

      // Check if start date is earlier than end date
      if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
        newErrors[`schoolYearDateRange_${i}`] = "Start Date must be earlier than End Date";
      }

      // Check for duplicate start dates
      if (startDate) {
        const duplicateIndex = startDatesArray.findIndex(
          (date, idx) => idx !== i && date.date === startDate
        );

        if (duplicateIndex !== -1) {
          newErrors[`schoolYearStartDate_${i}`] = `Start Date must be unique. Same date used in Date Pair ${duplicateIndex + 1}`;
        }
      }
    });

    setErrors(newErrors);
  };

  const validateDatePair = (index, startDate, endDate, startDatesArray, checkDuplicates = false) => {
    const newErrors = { ...errors };

    // Check if start date is earlier than end date
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      newErrors[`schoolYearDateRange_${index}`] = "Start Date must be earlier than End Date";
    } else {
      delete newErrors[`schoolYearDateRange_${index}`];
    }

    // Check for duplicate start dates (only when editing start dates)
    if (checkDuplicates && startDate) {
      const duplicateIndex = startDatesArray.findIndex(
        (date, i) => i !== index && date.date === startDate
      );

      if (duplicateIndex !== -1) {
        newErrors[`schoolYearStartDate_${index}`] = `Start Date must be unique. Same date used in Date Pair ${duplicateIndex + 1}`;
      } else {
        delete newErrors[`schoolYearStartDate_${index}`];
      }
    }

    setErrors(newErrors);
  };

  const onChangeText = (e) => {
    const { name, value } = e.target;

    if (name === "name" || name === "permalink") {

      const sanitizedValue = value.replace(/[\/\\]/g, ""); 

      setFormData({ ...formData, [name]: sanitizedValue });

      if (name === "name") {

        setFormData((prevFormData) => ({

          ...prevFormData,

          permalink: sanitizeUrl(sanitizedValue),

        }));

      }

    } else {

      setFormData({ ...formData, [name]: value });

    }

    if (name === "schoolYear") {
      const regex = /^\d{4}-\d{4}$/;
      if (!regex.test(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          schoolYear: "School Year must be in the format yyyy-yyyy",
        }));
      } else {
        // Check if the format is from earlier year to later year
        const [startYear, endYear] = value.split("-");
        const start = parseInt(startYear, 10);
        const end = parseInt(endYear, 10);
        if (end <= start) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            schoolYear:
              "Invalid school year range. Start year must be earlier than end year.",
          }));
        } else {
          setErrors((prevErrors) => {
            const { schoolYear, ...rest } = prevErrors;
            return rest;
          });
        }
      }
    } else if (name === "enrollmentStart" || name === "enrollmentEnd") {
      validateDates(name, value);
    }
  };

  const validateDates = (name, value) => {
    let newErrors = {};

    const updatedFormData = { ...formData, [name]: value };

    if (
      new Date(updatedFormData.enrollmentStart) >=
      new Date(updatedFormData.enrollmentEnd)
    ) {
      newErrors.enrollment =
       "Enrollment Start Date must be earlier than Enrollment End Date";
    } else {
      delete errors.enrollment;
    }

    setErrors(newErrors);
  };

// Check if user is Admin or Super Admin
  useEffect(() => {
    if (user && user.userStatus !== "Admin" && user.userStatus !== "Super Admin") {
      toast.error("Access Denied: Only Admins and Super Admins can edit registration profiles.");
    //  alert("Access Denied: Only Admins and Super Admins can edit registration profiles.");
      navigate("/intersession");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (LeloRegistrations.length === 0) {
      getLeloRegistrations();
    } else {
      const registration = LeloRegistrations.find(
        (registration) => registration._id === sessionId
      );

      if (registration) {
        // Handle both old string format and new array format
        const startDates = Array.isArray(registration.schoolYearStartDate)
          ? registration.schoolYearStartDate
          : [{ date: registration.schoolYearStartDate }];

        const endDates = Array.isArray(registration.schoolYearEndDate)
          ? registration.schoolYearEndDate
          : [{ date: registration.schoolYearEndDate }];

        setFormData({
          name: registration.name,
          status: registration.status,
          schoolYear: registration.schoolYear,
          permalink: registration.permalink,
          enrollmentStart: (registration.enrollmentStart),
          enrollmentEnd:(registration.enrollmentEnd),
          schoolYearStartDate: startDates,
          schoolYearEndDate: endDates,
        });
      }
    }
  }, [sessionId, LeloRegistrations]);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(errors).length === 0) {
      const isDuplicateName = LeloRegistrations.some(
        (registration) =>
          registration.name === formData.name && registration._id !== sessionId
      );

      if (isDuplicateName) {
        toast.error("A session with the same name already exists. Please use a different name.");
       // alert("A session with the same name already exists. Please use a different name.");
      } else {
        try {
          toast.success("Updating Session successfully!");
         // alert("Updating Session successfully!");
          await updateLELO(sessionId, formData);
          navigate("/intersession");
        } catch (error) {
          console.error("Error updating registration data", error.message);
          ErrorLogger.logError(error.message);
        }
      }
    } else {
      console.error("Validation errors", errors);
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Edit LELO Registration</title>
      </Helmet>
      <div className="location-edit">
        <div className="top-container">
          <div
            className="right display-back"
            style={{ fontSize: "18px" }}
            onClick={() => {
              navigate(-1);
            }}
          >
            <FaIcons.FaArrowAltCircleLeft className="back-icon" />
            <h4>Back</h4>
          </div>
        </div>

        <form onSubmit={onSubmit} className="form">
          <div className="edit-block" onClick={() => navigate(-1)}>
            <h4>Cancel Edit</h4>
          </div>

          <div className="lower-wrap">
            <div className="region-charter">
              <div className="region box">
                <h4>
                  Session Name<span style={{ color: "red" }}>*</span>
                </h4>
                <input
                  style={{ fontSize: "17px" }}
                  type="text"
                  name="name"
                  value={name}
                  onChange={onChangeText}
                  required
                />
              </div>

              <div className="status box">
                <h4>
                  Status<span style={{ color: "red" }}>*</span>
                </h4>
                <div className="dropdown-symbol">
                  <select
                    style={{ fontSize: "17px" }}
                    name="status"
                    value={status}
                    onChange={onChangeText}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="region box">
                <h4>
                  Permalink Name<span style={{ color: "red" }}>*</span>
                </h4>
                <input
                  style={{ fontSize: "17px" }}
                  type="text"
                  name="permalink"
                  value={permalink}
                  onChange={onChangeText}
                  maxLength={80}
                  placeholder={sanitizeUrl(name)}
                  required
                />
              </div>

              <div className="right box">
                <h4>
                  School Year <span style={{ color: "red" }}>*</span>
                </h4>
                <input
                  style={{ fontSize: "17px" }}
                  type="text"
                  name="schoolYear"
                  value={schoolYear}
                  onChange={onChangeText}
                  placeholder="yyyy-yyyy"
                  required
                />
                {errors.schoolYear && (
                  <span style={{ color: "red", fontSize: "14px" }}>
                    {errors.schoolYear}
                  </span>
                )}
              </div>

              <div className="right box">
                <h4>
                  Enrollment Start <span style={{ color: "red" }}>*</span>
                </h4>
                <input
                  style={{ fontSize: "17px" }}
                  type="date"
                  name="enrollmentStart"
                  value={enrollmentStart}
                  onChange={onChangeText}
                  max="9999-12-31"
                  required
                />
                {errors.enrollment && (
                  <span style={{ color: "red", fontSize: "14px" }}>
                    {errors.enrollment}
                  </span>
                )}
              </div>

              <div className="right box">
                <h4>
                  Enrollment End <span style={{ color: "red" }}>*</span>
                </h4>
                <input
                  style={{ fontSize: "17px" }}
                  type="date"
                  name="enrollmentEnd"
                  value={enrollmentEnd}
                  onChange={onChangeText}
                  max="9999-12-31"
                  required
                />
                {errors.enrollment && (
                  <span style={{ color: "red", fontSize: "14px" }}>
                    {errors.enrollment}
                  </span>
                )}
              </div>

              {schoolYearStartDate.map((startDate, index) => (
                <React.Fragment key={index}>
                  <div className="right box">
                    <h4>
                      School Year Start Date {index + 1} <span style={{ color: "red" }}>*</span>                      
                    </h4>
                    
                    <input
                      style={{ fontSize: "17px" }}
                      type="date"
                      value={startDate.date}
                      onChange={(e) =>
                        onChangeDateField(index, "schoolYearStartDate", e.target.value)
                      }
                      max="9999-12-31"
                      required
                    />
                    {errors[`schoolYearStartDate_${index}`] && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors[`schoolYearStartDate_${index}`]}
                      </span>
                    )}
                    {errors[`schoolYearDateRange_${index}`] && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors[`schoolYearDateRange_${index}`]}
                      </span>
                    )}
                  </div>

                  <div className="right box">
                     <div style={{ display: "flex", alignItems: "center", gap: "120px" }}>
                    <h4>
                      School Year End Date {index + 1}<span style={{ color: "red" }}>*</span>
                    </h4>
                    
                    {schoolYearStartDate.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDatePair(index)}
                          style={{
                            marginLeft: "10px",
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            padding: "5px 10px",
                            cursor: "pointer",
                            borderRadius: "4px",
                          }}
                        >
                          <FaIcons.FaTrash />
                        </button>
                      )}
                      </div>
                    <input
                      style={{ fontSize: "17px" }}
                      type="date"
                      value={schoolYearEndDate[index]?.date || ""}
                      onChange={(e) =>
                        onChangeDateField(index, "schoolYearEndDate", e.target.value)
                      }
                      max="9999-12-31"
                      required
                    />
                    {errors[`schoolYearDateRange_${index}`] && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {errors[`schoolYearDateRange_${index}`]}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              ))}

              <div className="right box">
                <button
                  type="button"
                  onClick={addDatePair}
                  style={{
                    background: "#537131",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    fontSize: "16px",
                  }}
                >
                  <FaIcons.FaPlus /> Add Another Date Pair
                </button>
              </div>
            </div>

            <input
              type="submit"
              value="Submit"
              className="btn btn-green"
              style={{ width: "100%" }}
              disabled={Object.keys(errors).length > 0}
            />
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default EditLELORegistrationProfile;
